/**
 * Approximate token counter for paste-panel warnings.
 * Uses the widely-cited 4-chars-per-token heuristic — off by ~10-20% from
 * tiktoken-exact counts but fine for UI thresholds. If we ever need exact
 * counts we can bundle js-tiktoken (~2MB) or call a server endpoint.
 */

const CHARS_PER_TOKEN = 4;

export function estimateTokens(text: string): number {
  if (!text) return 0;
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

export const TOKEN_THRESHOLDS = {
  SOFT_WARN: 30_000,
  COST_WARN: 100_000,
  HARD_CAP: 180_000,
} as const;

export type TokenStatus = 'ok' | 'soft_warn' | 'cost_warn' | 'hard_cap';

export function tokenStatus(tokens: number): TokenStatus {
  if (tokens >= TOKEN_THRESHOLDS.HARD_CAP) return 'hard_cap';
  if (tokens >= TOKEN_THRESHOLDS.COST_WARN) return 'cost_warn';
  if (tokens >= TOKEN_THRESHOLDS.SOFT_WARN) return 'soft_warn';
  return 'ok';
}

/**
 * Rough cost estimate (USD) assuming Claude Sonnet transform pricing.
 * Input: $3/1M, Output: $15/1M. We estimate output ~= 25% of input for a transform.
 */
export function estimateCostUsd(inputTokens: number): number {
  const outputTokens = Math.ceil(inputTokens * 0.25);
  return (inputTokens * 3) / 1_000_000 + (outputTokens * 15) / 1_000_000;
}
